const ELEMENT_RE = /\{([^}]+)\}/g;

export function getFormulaElements(formula, cells) {
  const uniformMatch = formula ? formula.match(/uniform\(\/(\d+)\)/) : null;
  if (uniformMatch && cells) {
    return cells.map((cell) => cell.label);
  }
  return [...(formula || '').matchAll(ELEMENT_RE)].map((match) => match[1]);
}

// Templates are user-editable: only arithmetic expressions are accepted here.
function safeEvalArithmetic(expression) {
  if (!/^[\d+\-*/().\s]*$/.test(expression)) {
    throw new Error('Invalid formula: ' + expression);
  }

  let index = 0;
  const skipSpaces = () => {
    while (index < expression.length && /\s/.test(expression[index])) index++;
  };
  const parseNumber = () => {
    const start = index;
    while (index < expression.length && /[\d.]/.test(expression[index])) index++;
    if (start === index) throw new Error('Invalid formula: ' + expression);
    return parseFloat(expression.slice(start, index));
  };
  const parseFactor = () => {
    skipSpaces();
    if (expression[index] === '(') {
      index++;
      const value = parseExpression();
      skipSpaces();
      if (expression[index] !== ')') throw new Error('Invalid formula: ' + expression);
      index++;
      return value;
    }
    if (expression[index] === '-') {
      index++;
      return -parseFactor();
    }
    if (expression[index] === '+') {
      index++;
      return parseFactor();
    }
    return parseNumber();
  };
  const parseTerm = () => {
    let value = parseFactor();
    skipSpaces();
    while (expression[index] === '*' || expression[index] === '/') {
      const operator = expression[index++];
      const right = parseFactor();
      value = operator === '*' ? value * right : value / right;
      skipSpaces();
    }
    return value;
  };
  const parseExpression = () => {
    let value = parseTerm();
    skipSpaces();
    while (expression[index] === '+' || expression[index] === '-') {
      const operator = expression[index++];
      const right = parseTerm();
      value = operator === '+' ? value + right : value - right;
      skipSpaces();
    }
    return value;
  };

  skipSpaces();
  const result = parseExpression();
  skipSpaces();
  if (index !== expression.length || !Number.isFinite(result)) {
    throw new Error('Invalid formula: ' + expression);
  }
  return result;
}

export function evalGradeFormula(formula, elements, values) {
  if (!formula) return 0;

  const uniformMatch = formula.match(/uniform\(\/(\d+)\)/);
  if (uniformMatch) {
    const maxValue = parseInt(uniformMatch[1], 10);
    if (!maxValue) return 0;
    let grade = 0;
    for (const element of elements) {
      grade += parseFloat(values[element] || 0) * 20 / maxValue;
    }
    grade /= elements.length || 1;
    return Math.round(grade * 100) / 100;
  }

  let expression = formula;
  for (const element of elements) {
    expression = expression.replaceAll('{' + element + '}', String(parseFloat(values[element] || 0)));
  }
  try {
    return Math.round(safeEvalArithmetic(expression) * 100) / 100;
  } catch (_) {
    return 0;
  }
}

export function getSummaryFormula(summaryTemplate) {
  const line = (summaryTemplate || '')
    .split('\n')
    .find((candidate) => candidate.trim().startsWith('note:'));
  if (!line) return '';
  return line.slice(line.indexOf(':') + 1).trim();
}

export function calculateStudentGrades(project, student) {
  const page = project?.pages?.[student] || {};
  const sections = {};

  for (const section of project?.student_template || []) {
    const elements = getFormulaElements(section.note, section.cells);
    const values = {};
    for (const element of elements) {
      values[element] = page[section.name + ':' + element + ':note']?.value;
    }
    sections[section.name] = evalGradeFormula(section.note, elements, values);
  }

  const totalFormula = getSummaryFormula(project?.summary_template);
  const totalElements = getFormulaElements(totalFormula);
  const total = evalGradeFormula(totalFormula, totalElements, sections);
  return { sections, total };
}

export function getScoreBadgeClass(score) {
  if (score === null || score === undefined || Number.isNaN(Number(score))) return '';
  const numericScore = Number(score);
  if (numericScore >= 16) return 'score-high';
  if (numericScore >= 12) return 'score-med';
  if (numericScore >= 10) return 'score-low';
  return 'score-fail';
}
